import { useTu } from "@src/appV2/i18n/useTu";
import { startOfDay } from "@clipboard-health/date-time";
import { isDefined, isEmpty } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Autocomplete, CircularProgress, MenuItem, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFetchWorkplaceWorkerTypes } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import type { TuFunction } from "@src/appV2/i18n/types";
import { useDebounce } from "@src/appV2/lib/utils";
import { getQualificationsAvailableToDisplay } from "@src/appV2/Shifts/utils/getQualificationsAvailableToDisplay";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetFacilityProfile } from "../../../FacilityProfile/api/useGetFacilityProfile";
import { Text } from "../../components/Text";
import { formatAgentRequestLabel } from "../../DailyView/utils/formatAgentRequestLabel";

import {
  type ShiftMigrationChangeOfOwnership,
  type ShiftMigrationDestinationWorkplace,
  type ShiftMigrationOriginWorkplace,
} from "../types";

import { fromWorkplaceDayPickerValue, toWorkplaceDayPickerValue } from "../utils/workplaceDay";

import {
  getParametersFormSchema,
  type ShiftMigrationParametersFormValues,
  type ShiftMigrationParametersSubmitValues,
} from "./shiftMigrationParametersForm";

// Technical projection string for the facility-profile endpoint, not user-facing copy.
// Shared by both profile lookups so they hit the same react-query cache entry.
// Space-separated: the facility-profile API splits projections on spaces, so a
// comma-joined value is treated as one (forbidden) field and 400s.
const I18N_SKIP_FACILITY_PROFILE_PROJECTION = "name userId fullAddress";

interface ShiftMigrationParametersStepProps {
  origin: ShiftMigrationOriginWorkplace;
  /**
   * Origin's change-of-ownership record. Pre-fills the destination and
   * effective-starting fields when no `defaults` are given; both stay editable.
   */
  changeOfOwnership?: ShiftMigrationChangeOfOwnership;
  defaults?: Partial<ShiftMigrationParametersFormValues>;
  isSubmitting: boolean;
  onSubmit: (values: ShiftMigrationParametersSubmitValues) => void;
  formId: string;
}

function getNoOptionsText(searchInput: string, isLoading: boolean, tu: TuFunction): string {
  if (isEmpty(searchInput)) {
    return tu("Start typing to search for workplaces");
  }
  return isLoading ? tu("Searching workplaces...") : tu("No workplaces found");
}

/**
 * Converts a change-of-ownership effective datetime into the form's day
 * representation: UTC midnight of the instant's calendar day in the workplace's
 * tz (see utils/workplaceDay.ts), matching what the picker would store for the
 * same visible day.
 */
function getChangeOfOwnershipEffectiveStarting(
  effectiveDate: string | undefined,
  tmz: string
): Date | undefined {
  if (!isDefined(effectiveDate)) {
    return undefined;
  }
  const date = new Date(effectiveDate);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return startOfDay(date, { timeZone: tmz });
}

export function ShiftMigrationParametersStep(props: ShiftMigrationParametersStepProps) {
  const { tu } = useTu("shiftMigration");

  const {
    origin,
    defaults,
    changeOfOwnership: changeOfOwnershipProp,
    isSubmitting,
    onSubmit,
    formId,
  } = props;
  const parametersFormSchema = getParametersFormSchema(tu);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce({ debounceTimeInMs: 300, value: searchInput });

  // `defaults` carry a draft the user already submitted once (returning from the
  // preview step); never re-apply the change-of-ownership prefill over them.
  const changeOfOwnership = isDefined(defaults) ? undefined : changeOfOwnershipProp;

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ShiftMigrationParametersFormValues>({
    resolver: zodResolver(parametersFormSchema),
    defaultValues: {
      destination: defaults?.destination ?? null,
      effectiveStarting:
        defaults?.effectiveStarting ??
        getChangeOfOwnershipEffectiveStarting(changeOfOwnership?.effectiveDate, origin.tmz),
      effectiveEnding: defaults?.effectiveEnding ?? null,
      qualification: defaults?.qualification ?? "",
    },
    mode: "onChange",
  });

  const replacementFacilityId = changeOfOwnership?.replacementFacilityId;
  const shouldPrefillDestination =
    isDefined(replacementFacilityId) &&
    replacementFacilityId.length > 0 &&
    replacementFacilityId !== origin.id;

  const { data: replacementProfile } = useGetFacilityProfile(
    replacementFacilityId ?? "",
    I18N_SKIP_FACILITY_PROFILE_PROJECTION,
    { enabled: shouldPrefillDestination }
  );

  const [didPrefillDestination, setDidPrefillDestination] = useState(false);
  // Sticky flag: any destination interaction (selecting, clearing, or typing a
  // search) before the replacement lookup resolves permanently disables the
  // prefill, even if the field is empty again by the time it resolves.
  const didUserEditDestinationRef = useRef(false);

  // Pre-populate the destination once from the change-of-ownership replacement.
  // The admin can still clear or change it afterwards.
  useEffect(() => {
    if (didPrefillDestination || !shouldPrefillDestination || !isDefined(replacementProfile)) {
      return;
    }
    setDidPrefillDestination(true);
    if (didUserEditDestinationRef.current || isDefined(getValues("destination"))) {
      return;
    }
    setValue(
      "destination",
      { id: replacementProfile.userId, name: replacementProfile.name },
      { shouldValidate: true }
    );
  }, [didPrefillDestination, shouldPrefillDestination, replacementProfile, getValues, setValue]);

  const { data: facilitiesData, isFetching: facilitiesLoading } = useSearchFacilities(
    { searchInput: debouncedSearch },
    { enabled: debouncedSearch.length > 0 }
  );

  const destinationOptions: ShiftMigrationDestinationWorkplace[] =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .filter((facility: Facility) => facility.userId !== origin.id)
      .map((facility: Facility) => ({ id: facility.userId, name: facility.name })) ?? [];

  const watchedDestination = watch("destination");

  const { data: destinationProfile } = useGetFacilityProfile(
    watchedDestination?.id ?? "",
    I18N_SKIP_FACILITY_PROFILE_PROJECTION,
    { enabled: isDefined(watchedDestination?.id) }
  );

  const destinationAddress = destinationProfile?.fullAddress?.formatted;

  const addressMismatch =
    isDefined(destinationAddress) &&
    isDefined(origin.address) &&
    origin.address.trim().toLowerCase() !== destinationAddress.trim().toLowerCase();

  // Qualifications enabled for the origin facility, derived the same way the
  // calendar / Daily View builds its qualification tabs: visible worker types
  // that are available to display (i.e. contracted). See DailyViewContainer.
  const { data: workerTypesData } = useFetchWorkplaceWorkerTypes(
    { workplaceId: origin.id, showHidden: true },
    { enabled: isDefined(origin.id) }
  );

  const qualificationOptions = useMemo(() => {
    const workerTypes = workerTypesData?.data.workerTypes ?? [];
    const available = getQualificationsAvailableToDisplay(workerTypes);
    const names = new Set(
      workerTypes
        .filter((workerType) => workerType.visible && available.has(workerType.name))
        .map((workerType) => workerType.name)
    );
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [workerTypesData]);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit((values) => {
        if (!isDefined(values.destination)) {
          return;
        }
        onSubmit({
          destination: values.destination,
          effectiveStarting: values.effectiveStarting,
          effectiveEnding: values.effectiveEnding ?? undefined,
          qualification: isEmpty(values.qualification) ? undefined : values.qualification,
        });
      })}
    >
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Origin workplace")}
          </Text>
          <Text variant="body2">{origin.name}</Text>
          <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
            {origin.address ?? tu("Address unavailable")}
          </Text>
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Destination workplace")}
          </Text>
          <Controller
            control={control}
            name="destination"
            render={({ field }) => (
              <Autocomplete
                autoComplete
                disabled={isSubmitting}
                filterOptions={(options) => options}
                getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === "string" || typeof value === "string") {
                    return false;
                  }
                  return option.id === value.id;
                }}
                loading={facilitiesLoading}
                loadingText={tu("Searching workplaces...")}
                noOptionsText={getNoOptionsText(searchInput, facilitiesLoading, tu)}
                options={destinationOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={isDefined(errors.destination)}
                    helperText={errors.destination?.message}
                    placeholder={tu("Start typing workplace name...")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {facilitiesLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(renderProps, option) => {
                  if (!option) {
                    return null;
                  }
                  return (
                    <li {...renderProps} key={option.id}>
                      <Stack direction="column">
                        <Text variant="body2">{option.name}</Text>
                        <Text
                          sx={(theme) => ({ color: theme.palette.text.secondary })}
                          variant="caption"
                        >
                          {tu("ID: {{id}}", { values: { id: option.id } })}
                        </Text>
                      </Stack>
                    </li>
                  );
                }}
                value={field.value}
                onChange={(_, newValue) => {
                  didUserEditDestinationRef.current = true;
                  if (typeof newValue !== "string") {
                    field.onChange(newValue);
                  }
                }}
                onInputChange={(_, newInputValue, reason) => {
                  // Only typed input counts as a user edit; "reset" also fires
                  // when the prefill itself populates the field.
                  if (reason === "input") {
                    didUserEditDestinationRef.current = true;
                  }
                  setSearchInput(newInputValue);
                }}
              />
            )}
          />
          {isDefined(destinationAddress) && (
            <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
              {destinationAddress}
            </Text>
          )}
          {addressMismatch && (
            <Alert severity="warning">
              {tu(
                "Origin and destination addresses don't match. The backend will reject the migration unless the addresses are compatible."
              )}
            </Alert>
          )}
        </Stack>

        <Stack direction="row" spacing={3}>
          <Controller
            control={control}
            name="effectiveStarting"
            render={({ field }) => (
              <DatePicker
                disabled={isSubmitting}
                label={tu("Effective starting")}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: isDefined(errors.effectiveStarting),
                    helperText: errors.effectiveStarting?.message,
                  },
                }}
                value={toWorkplaceDayPickerValue(field.value, origin.tmz)}
                onChange={(date) => {
                  field.onChange(fromWorkplaceDayPickerValue(date, origin.tmz) ?? undefined);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="effectiveEnding"
            render={({ field }) => (
              <DatePicker
                disabled={isSubmitting}
                label={tu("Effective ending (optional)")}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: isDefined(errors.effectiveEnding),
                    helperText: errors.effectiveEnding?.message,
                  },
                }}
                value={toWorkplaceDayPickerValue(field.value, origin.tmz)}
                onChange={(date) => {
                  field.onChange(fromWorkplaceDayPickerValue(date, origin.tmz));
                }}
              />
            )}
          />
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Qualification")}
          </Text>
          <Controller
            control={control}
            name="qualification"
            render={({ field }) => (
              <TextField
                select
                disabled={isSubmitting}
                helperText={tu(
                  "Limit the migration to a single qualification, or migrate every qualification."
                )}
                name={field.name}
                size="small"
                value={field.value ?? ""}
                onChange={field.onChange}
              >
                <MenuItem value="">{tu("Any qualification")}</MenuItem>
                {qualificationOptions.map((qualification) => (
                  <MenuItem key={qualification} value={qualification}>
                    {formatAgentRequestLabel(qualification)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </Stack>
    </form>
  );
}
